import {
  AsyncPipe,
  CurrencyPipe,
  DatePipe,
  DecimalPipe,
} from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Observable, Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  PaidTier,
  SchoolBilling,
} from '../../../../models/billing.school.model';
import { Group } from '../../../../models/group.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { Product } from '../../../../models/product.model';
import { UserModel } from '../../../../models/user.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { GroupService } from '../../../../services/group.service';
import { MemberService } from '../../../../services/member.service';
import { ProductService } from '../../../../services/product.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { StripeService } from '../../../../services/stripe.service';
import { UtilsService } from '../../../../services/utils.service';
import * as groupActions from '../../../../state/actions/group.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import {
  BtnFabComponent,
  BtnFabPosition,
} from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnIconComponent } from '../../../components/buttons/btn-icon/btn-icon.component';
import { BtnTextComponent } from '../../../components/buttons/btn-text/btn-text.component';
import { AlertType, DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import {
  ChipColor,
  ChipComponent,
} from '../../../components/inputs/chip/chip.component';
import { LoadingContainerComponent } from '../../../components/loaders/loading-container/loading-container.component';
import { LoadingPopupComponent } from '../../../components/loaders/loading-popup/loading-popup.component';

@Component({
  selector: 'app-view-account',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnTextComponent,
    BtnActionComponent,
    BtnIconComponent,
    MomentPipe,
    AsyncPipe,
    DatePipe,
    CurrencyPipe,
    DecimalPipe,
    LoadingContainerComponent,
    BtnChipComponent,
    BtnActionComponent,
    ChipComponent,
    BtnFabComponent,
    RouterLink,
  ],
  templateUrl: './view-account.component.html',
  styleUrl: './view-account.component.css',
})
export class ViewAccountComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  store: Store<AppState> = inject(Store<AppState>);
  roles: SchoolRolesService = inject(SchoolRolesService);
  groupService: GroupService = inject(GroupService);
  private utilsService = inject(UtilsService);
  stripeService: StripeService = inject(StripeService);
  memberService: MemberService = inject(MemberService);
  productService: ProductService = inject(ProductService);
  private afs: AngularFirestore = inject(AngularFirestore);
  private toastr: ToastrService = inject(ToastrService);
  private logger: NGXLogger = inject(NGXLogger);

  private dialogRef?: MatDialogRef<LoadingPopupComponent>;
  private dialog: MatDialog = inject(MatDialog);

  groupId: string | null | undefined;

  billingDetails: SchoolBilling | undefined;
  subscriptions: any[] = [];
  bills: any[] = [];
  memberNamesMap: Map<string, string> = new Map();
  groupMetadata$: Observable<any> = new Observable();
  groupMetadata: any;
  groupTokens$: Observable<any> = new Observable();
  groupTokens: any;

  // Paginated usage logs
  selectedInvoice: any = null;
  usageRecords: any[] = [];
  lastVisibleDoc: any = null;
  hasMoreUsage = false;
  loadingUsage = false;

  loadingBilling$ = new Observable<boolean>();
  private actions$: Actions = inject(Actions);

  group$: Observable<Group | undefined> = new Observable();
  group: Group | undefined;
  user: UserModel | null = null;
  myRoles$: Observable<any> = new Observable();

  membershipStatus = MembershipStatus;
  tiers = PaidTier;

  exit$: Subject<void> = new Subject();

  get freeSeats(): number {
    return this.getFreeSeatsCount();
  }

  getProductIdFromItem(item: any): string | undefined {
    if (!item?.price?.product) return undefined;
    if (typeof item.price.product === 'string') return item.price.product;
    if (typeof item.price.product === 'object' && item.price.product.id) return item.price.product.id;
    return undefined;
  }

  findProductById(productId: string | undefined): Product | undefined {
    if (!productId) return undefined;
    return this.productService.allProducts().find((p) => p.id === productId);
  }

  getSeatsProduct(): Product | undefined {
    return this.productService.allProducts().find(p => {
      if (p.metadata?.productType === 'seats') return true;
      return p.id === (environment.STRIPE_SCHOOL_SEATS_PRICE_ID || 'prod_TQqEowVFMUF4i0');
    });
  }

  getFreeSeatsCount(): number {
    const seatsProduct = this.getSeatsProduct();
    if (!seatsProduct || !seatsProduct.prices || seatsProduct.prices.length === 0) {
      return this.group?.groupType === 'church' ? 100 : (environment.STRIPE_SCHOOL_FREE_SEATS || 25);
    }
    const currency = (this.group?.currency || 'usd').toLowerCase();
    const selectedInterval = 'month';
    let price = seatsProduct.prices.find(
      (pr) => pr.active && 
              pr.currency.toLowerCase() === currency && 
              pr.recurring?.interval === selectedInterval
    );
    if (!price) {
      price = seatsProduct.prices[0];
    }

    if (price && price.tiers && price.tiers.length > 0) {
      let freeSeats = 0;
      for (const tier of price.tiers) {
        const isFree = (!tier.unit_amount || tier.unit_amount === 0) && (!tier.flat_amount || tier.flat_amount === 0);
        if (isFree && tier.up_to !== null) {
          freeSeats = tier.up_to;
        } else if (!isFree) {
          break;
        }
      }
      if (freeSeats > 0) {
        return freeSeats;
      }
    }
    
    return this.group?.groupType === 'church' ? 100 : (environment.STRIPE_SCHOOL_FREE_SEATS || 25);
  }

  chipColor = ChipColor;
  position = BtnFabPosition;

  constructor() {
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      this.groupId = params.get('id');
    });
    this.group$ = this.store.select(groupSelector.getSelectedGroup);
    this.group$.pipe(takeUntil(this.exit$)).subscribe((group) => {
      this.group = group;
    });
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => (this.user = user));
    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
    this.groupMetadata$ = this.store.select(
      groupSelector.getSelectedGroupMetadata,
    );
    this.groupMetadata$.pipe(takeUntil(this.exit$)).subscribe((metadata) => {
      this.groupMetadata = metadata;
    });
    this.groupTokens$ = this.store.select(
      groupSelector.getSelectedGroupTokens,
    );
    this.groupTokens$.pipe(takeUntil(this.exit$)).subscribe((tokens) => {
      this.groupTokens = tokens;
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  getGroupProducts(): Product[] {
    return this.group?.groupType === 'church'
      ? this.productService.churchProducts()
      : this.productService.schoolProducts();
  }

  ngOnInit() {
    this.productService.refresh();
    this.loadGroupSubscriptions();
    this.loadGroupBills();
    this.loadMemberships();
    if (this.groupId) {
      this.store.dispatch(
        groupActions.getGroupStats({ groupId: this.groupId }),
      );
    }
  }

  loadGroupSubscriptions() {
    this.stripeService
      .getGroupSubscriptions(this.groupId!)
      .then((subs) => {
        this.logger.debug(
          'Group subscriptions Count:',
          JSON.stringify(subs?.length),
        );
        this.subscriptions = subs;
      })
      .catch((err) => {
        this.logger.error('Error fetching group subscriptions:', err);
        console.error('Error fetching group subscriptions:', err);
        this.toastr.error(err.message || 'Error fetching group subscriptions');
      });
  }

  loadGroupBills() {
    this.stripeService
      .getGroupBills(this.groupId!)
      .then((bills) => {
        this.bills = bills;
      })
      .catch((err) => {
        this.logger.error('Error fetching group bills:', err);
        console.error('Error fetching group bills:', err);
        this.toastr.error(err.message || 'Error fetching group bills');
      });
  }

  loadMemberships() {
    this.memberService.getMembersByGroup(this.groupId!).subscribe({
      next: (members) => {
        members.forEach((m) => {
          this.memberNamesMap.set(m.memberId, m.name || m.email || m.memberId);
        });
      },
    });
  }

  isSeatsSubscriptionItem(item: any): boolean {
    const seatsId = environment.STRIPE_SCHOOL_SEATS_PRICE_ID;
    const productId = this.getProductIdFromItem(item);
    const priceId = item?.price?.id;

    if (productId === seatsId || priceId === seatsId) return true;
    
    const seatsProduct = this.getSeatsProduct();
    if (seatsProduct) {
      if (productId === seatsProduct.id || priceId === seatsProduct.id) return true;
      if (seatsProduct.prices?.some(pr => pr.id === priceId)) return true;
    }
    return false;
  }

  getStorageLimitGb(): number {
    if (this.groupMetadata?.storageLimitGb) {
      return this.groupMetadata.storageLimitGb;
    }
    let totalStorageGb = 0;
    this.subscriptions.forEach((sub) => {
      const subObj = sub.subscription || sub;
      const status = subObj?.status;
      if (status === 'active' || status === 'trialing') {
        const items = subObj?.items?.data;
        items?.forEach((item: any) => {
          const productId = this.getProductIdFromItem(item);
          const quantity = item.quantity || 1;
          if (productId) {
            const prod = this.findProductById(productId);
            if (prod) {
              const prodMetadata = prod.metadata || {};
              const productType = prodMetadata.productType;
              const capacity = parseFloat(prodMetadata.capacity || '0');
              if (productType === 'storage') {
                totalStorageGb += capacity * quantity;
              }
            }
          }
        });
      }
    });
    return totalStorageGb > 0 ? totalStorageGb : 5;
  }

  getStorageUsageBytes(): number {
    return this.groupMetadata?.storageUsageBytes || 0;
  }

  getStorageUsageGb(): number {
    const bytes = this.getStorageUsageBytes();
    return bytes / (1024 * 1024 * 1024);
  }

  getStorageUsagePercent(): number {
    const limit = this.getStorageLimitGb();
    if (limit <= 0) return 0;
    return Math.min(100, (this.getStorageUsageGb() / limit) * 100);
  }

  getFreeStorageBytes(): number {
    const limitBytes = this.getStorageLimitGb() * 1024 * 1024 * 1024;
    const usageBytes = this.groupMetadata?.storageUsageBytes || 0;
    return Math.max(0, limitBytes - usageBytes);
  }

  getAiCreditsLimit(): number {
    let totalAiCredits = 0;
    this.subscriptions.forEach((sub) => {
      const subObj = sub.subscription || sub;
      const status = subObj?.status;
      if (status === 'active' || status === 'trialing') {
        const items = subObj?.items?.data;
        items?.forEach((item: any) => {
          const productId = this.getProductIdFromItem(item);
          const quantity = item.quantity || 1;
          if (productId) {
            const prod = this.findProductById(productId);
            if (prod) {
              const prodMetadata = prod.metadata || {};
              const productType = prodMetadata.productType;
              const capacity = parseFloat(prodMetadata.capacity || '0');
              if (productType === 'ai_credits') {
                totalAiCredits += capacity * quantity;
              }
            }
          }
        });
      }
    });
    return totalAiCredits;
  }

  getAiCreditsUsed(): number {
    return this.groupTokens?.aiCreditsUsed ?? this.groupMetadata?.aiCreditsUsed ?? 0;
  }

  getAiCreditsActive(): number {
    if (this.groupTokens?.availableCredits !== undefined) {
      return this.groupTokens.availableCredits;
    }
    if (this.groupTokens?.activeCredits !== undefined) {
      return this.groupTokens.activeCredits;
    }
    if (this.groupMetadata?.availableCredits !== undefined) {
      return this.groupMetadata.availableCredits;
    }
    if (this.groupMetadata?.activeCredits !== undefined) {
      return this.groupMetadata.activeCredits;
    }
    const limit = this.getAiCreditsLimit();
    const used = this.getAiCreditsUsed();
    return Math.max(0, limit - used);
  }

  showAiTransactionsModal = false;
  loadingAiTransactions = false;
  aiTransactions: any[] = [];

  async openAiTransactionsModal() {
    this.showAiTransactionsModal = true;
    if (!this.groupId) return;

    this.loadingAiTransactions = true;
    try {
      const snap = await lastValueFrom(
        this.afs
          .collection(`group_tokens/${this.groupId}/credit_ledger`, (ref) =>
            ref.orderBy('timestamp', 'desc').limit(50)
          )
          .snapshotChanges()
          .pipe(take(1))
      );

      const items = (snap || []).map((docSnap: any) => ({
        id: docSnap.payload.doc.id,
        ...(docSnap.payload.doc.data() as any),
      }));

      this.aiTransactions = items;
    } catch (err) {
      console.warn('ViewAccountComponent :: Error fetching AI transactions:', err);
      this.toastr.error('Failed to load AI usage transactions');
    } finally {
      this.loadingAiTransactions = false;
    }
  }

  closeAiTransactionsModal() {
    this.showAiTransactionsModal = false;
  }

  formatFeatureTitle(tx: any): string {
    const rawFeature = tx?.feature || tx?.metadata?.feature || '';
    if (!rawFeature) {
      return 'AI Feature';
    }
    const featureMap: Record<string, string> = {
      notice_generation: 'Notice Generation',
      notice_image: 'Notice Image',
      notice_image_generation: 'Notice Image Generation',
      // Legacy key kept for older ledger entries recorded before the vendor name was removed.
      notice_image_generation_nano_banana: 'Notice Image Generation',
      quiz_generation: 'Quiz Question Generation',
      quiz_grading: 'Quiz Grading',
      receipt_scan: 'Receipt Scanning',
      accounting_receipt_extraction: 'Receipt Extraction',
      ai_generation: 'AI Generation',
    };

    if (featureMap[rawFeature]) {
      return `AI Feature: ${featureMap[rawFeature]}`;
    }

    return `AI Feature: ${this.stripVendorNames(rawFeature)}`;
  }

  /**
   * Safety net for any feature key we haven't explicitly mapped above: formats it into a readable
   * title while stripping known third-party AI vendor/model names so they never surface in a
   * member-facing transaction description.
   */
  private stripVendorNames(rawFeature: string): string {
    const vendorTerms = [
      'nano banana',
      'nanobanana',
      'gemini',
      'imagen',
      'vertex ai',
      'vertexai',
      'chatgpt',
      'openai',
      'gpt-4',
      'gpt4',
      'gpt',
      'claude',
      'anthropic',
      'dall-e',
      'dalle',
      'stability ai',
      'midjourney',
    ];

    let cleaned = rawFeature.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
    for (const term of vendorTerms) {
      cleaned = cleaned.replace(new RegExp(term, 'gi'), '');
    }

    return cleaned
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'AI Generation';
  }

  getAiCreditsUsagePercent(): number {
    const limit = this.getAiCreditsLimit();
    if (limit <= 0) return 0;
    const active = this.getAiCreditsActive();
    return Math.min(100, (active / limit) * 100);
  }

  getSeatsLimit(): number {
    const details = this.getSeatsSubscriptionDetails();
    return details?.quantity || this.freeSeats;
  }

  getSeatsUsed(): number {
    return this.group?.groupType === 'church'
      ? (this.group?.activeMembers ?? 0)
      : (this.group?.students ?? 0);
  }

  getSeatsUsagePercent(): number {
    const limit = this.getSeatsLimit();
    if (limit <= 0) return 0;
    return Math.min(100, (this.getSeatsUsed() / limit) * 100);
  }

  getFreeSeats(): number {
    return Math.max(0, this.getSeatsLimit() - this.getSeatsUsed());
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  getNextBillingDetails(): { date: number | null; products: string[] } | null {
    const now = Date.now();
    const thirtyDaysFromNow = now + 30 * 24 * 60 * 60 * 1000;

    let earliestBillingTime: number | null = null;
    let productsToBill: string[] = [];

    this.subscriptions.forEach((sub) => {
      const subObj = sub.subscription || sub;
      const status = subObj?.status;
      if (status === 'active' || status === 'trialing') {
        const periodEndSec = subObj?.current_period_end || sub?.current_period_end;
        if (!periodEndSec) return;
        const periodEndMs = periodEndSec * 1000;

        if (periodEndMs >= now && periodEndMs <= thirtyDaysFromNow) {
          if (
            earliestBillingTime === null ||
            periodEndMs < earliestBillingTime
          ) {
            earliestBillingTime = periodEndMs;
            productsToBill = [];
          }

          if (periodEndMs === earliestBillingTime) {
            subObj?.items?.data?.forEach((item: any) => {
              const productId = this.getProductIdFromItem(item);
              const prod = this.findProductById(productId);
              if (prod) {
                productsToBill.push(prod.name);
              } else if (this.isSeatsSubscriptionItem(item)) {
                productsToBill.push('Student Seats');
              } else if (typeof item.price?.product === 'object' && item.price.product?.name) {
                productsToBill.push(item.price.product.name);
              } else if (item.price?.nickname) {
                productsToBill.push(item.price.nickname);
              } else {
                productsToBill.push('Add-on Product');
              }
            });
          }
        }
      }
    });

    if (earliestBillingTime === null) {
      return null;
    }

    const uniqueProducts = Array.from(new Set(productsToBill));

    return {
      date: earliestBillingTime,
      products: uniqueProducts,
    };
  }

  isGroupSubscribedToSeats(): boolean {
    return this.subscriptions.some((sub) => {
      const subObj = sub.subscription || sub;
      return subObj?.items?.data?.some((item: any) =>
        this.isSeatsSubscriptionItem(item),
      );
    });
  }

  getSeatsSubscriptionDetails(): any {
    for (const sub of this.subscriptions) {
      const subObj = sub.subscription || sub;
      const items = subObj?.items?.data;
      const seatsItem = items?.find((item: any) =>
        this.isSeatsSubscriptionItem(item),
      );
      if (seatsItem) {
        const periodEndSec = subObj?.current_period_end || sub?.current_period_end;
        return {
          subscriptionId: sub.id,
          itemId: seatsItem.id,
          quantity: seatsItem.quantity || this.freeSeats,
          payerName: this.memberNamesMap.get(sub.uid) || 'Admin Payer',
          payerId: sub.uid,
          interval: seatsItem.price?.recurring?.interval || 'month',
          currentPeriodEnd: periodEndSec ? periodEndSec * 1000 : null,
        };
      }
    }
    return null;
  }

  getAvailableProducts(): Product[] {
    const allProds = this.getGroupProducts();
    // Exclude seats from available add-ons (seats are handled by standard subscribe tab)
    const addOnProds = allProds.filter(
      (p) => p.id !== environment.STRIPE_SCHOOL_SEATS_PRICE_ID,
    );

    return addOnProds.filter((prod) => {
      // Available if none of the active subscriptions contain this product ID
      return !this.subscriptions.some((sub) => {
        const subObj = sub.subscription || sub;
        return subObj?.items?.data?.some(
          (item: any) => this.getProductIdFromItem(item) === prod.id,
        );
      });
    });
  }

  getSubscribedProducts(): any[] {
    const list: any[] = [];
    this.subscriptions.forEach((sub) => {
      const subObj = sub.subscription || sub;
      subObj?.items?.data?.forEach((item: any) => {
        // Exclude seats from the general add-ons list as it's shown in main status
        if (this.isSeatsSubscriptionItem(item)) return;

        const rawProduct = item.price?.product;
        const productId = this.getProductIdFromItem(item);

        const prod = this.findProductById(productId);

        let productName = 'Unknown Product';
        if (prod?.name) {
          productName = prod.name;
        } else if (typeof rawProduct === 'object' && rawProduct?.name) {
          productName = rawProduct.name;
        } else if (item.price?.nickname) {
          productName = item.price.nickname;
        }

        let productDescription = 'Add-on Product';
        if (prod?.description) {
          productDescription = prod.description;
        } else if (typeof rawProduct === 'object' && rawProduct?.description) {
          productDescription = rawProduct.description;
        }

        list.push({
          subscriptionId: sub.id,
          itemId: item.id,
          productName,
          productDescription,
          payerId: sub.uid,
          payerName: this.memberNamesMap.get(sub.uid) || 'Admin Payer',
          priceId: item.price?.id,
          interval: item.price?.recurring?.interval || 'month',
          quantity: item.quantity,
          amount:
            item.price?.unit_amount !== null && item.price?.unit_amount !== undefined
              ? (item.price.unit_amount * (item.quantity || 1)) / 100
              : null,
          currency: item.price?.currency,
          usageType: item.price?.recurring?.usage_type || 'licensed',
        });
      });
    });
    return list;
  }

  onChangePlan() {
    this.router.navigate(['addonsubscribe'], { relativeTo: this.route });
  }

  onSubscribe() {
    this.router.navigate(['addonsubscribe'], { relativeTo: this.route });
  }

  onDeleteProduct(subscriptionId: string, itemId: string) {
    this.logger.debug('ViewAccountComponent.onDeleteProduct called:', { subscriptionId, itemId });
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Remove Add-On Product',
      description:
        'Are you sure you want to remove this product from the subscription? This will stop future billing for it.',
      action: 'Remove',
      showType: AlertType.warning,
      buttonColor: 'bg-red-500',
      buttonTextColor: 'text-white',
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.logger.debug('Confirmation accepted. Executing deleteSubscriptionItem...');
        this.show('Removing product...');
        this.stripeService
          .deleteSubscriptionItem(subscriptionId, itemId)
          .then((res) => {
            this.logger.info('Subscription item deleted successfully:', itemId, res);
            console.log('Subscription item deleted successfully:', itemId, res);
            this.hide();
            this.toastr.success('Product removed successfully');
            this.loadGroupSubscriptions();
          })
          .catch((error: any) => {
            this.logger.error('Error deleting subscription item:', error);
            console.error('onDeleteProduct error caught:', error);
            console.dir(error);
            this.hide();
            const message = error?.message || error?.details || 'Error deleting subscription item';
            this.toastr.error(message);
            this.utilsService.onShowAlert(error);
          });
      }
    });
  }

  // Paginated usage details
  async selectBill(invoice: any) {
    if (this.selectedInvoice?.id === invoice.id) {
      // Toggle off
      this.selectedInvoice = null;
      this.usageRecords = [];
      return;
    }

    await this.loadUsageRecords(invoice, false);
  }

  async loadUsageRecords(invoice: any, loadMore = false) {
    if (this.loadingUsage) return;
    this.loadingUsage = true;

    if (!loadMore) {
      this.selectedInvoice = invoice;
      this.usageRecords = [];
      this.lastVisibleDoc = null;
      this.hasMoreUsage = true;
    }

    const startOfMonth = new Date(invoice.created);
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const endOfMonth = new Date(invoice.created);
    endOfMonth.setMonth(endOfMonth.getMonth() + 1);
    endOfMonth.setDate(0);
    endOfMonth.setHours(23, 59, 59, 999);

    try {
      const collectionRef = this.afs
        .collection('billing_usage')
        .doc(this.groupId!)
        .collection('ai_tokens', (ref) => {
          let q = ref
            .where('timestamp', '>=', startOfMonth)
            .where('timestamp', '<=', endOfMonth)
            .orderBy('timestamp', 'desc');

          if (this.lastVisibleDoc) {
            q = q.startAfter(this.lastVisibleDoc);
          }
          return q.limit(10);
        });

      const snapshot = await lastValueFrom(collectionRef.get().pipe(take(1)));

      if (snapshot.empty) {
        this.hasMoreUsage = false;
      } else {
        this.lastVisibleDoc = snapshot.docs[snapshot.docs.length - 1];
        const newRecords = snapshot.docs.map((doc) => {
          const data: any = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || null,
          };
        });
        this.usageRecords = [...this.usageRecords, ...newRecords];

        if (snapshot.docs.length < 10) {
          this.hasMoreUsage = false;
        }
      }
    } catch (err) {
      this.logger.error('Error fetching usage records:', err);
    } finally {
      this.loadingUsage = false;
    }
  }

  formatPrice(amount: number, currency: string): string {
    if (amount === null || amount === undefined) return '';
    const code = (currency || 'usd').toLowerCase();
    let symbol = code.toUpperCase();
    switch (code) {
      case 'eur':
        symbol = '€';
        break;
      case 'gbp':
        symbol = '£';
        break;
      case 'jpy':
        symbol = '¥';
        break;
      case 'inr':
        symbol = '₹';
        break;
      case 'usd':
      case 'cad':
      case 'aud':
      case 'nzd':
        symbol = '$';
        break;
    }
    return `${symbol} ${amount.toFixed(2)}`;
  }

  show(message: string) {
    this.dialogRef = this.dialog.open(LoadingPopupComponent, {
      disableClose: true,
      data: { message },
    });
  }

  hide() {
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }
}
